import type { StateStorage } from 'zustand/middleware';

/** Unscoped localStorage key that records the last signed-in user id for hydration. */
export const STORAGE_USER_POINTER_KEY = 'storage-user-id';

let cachedStorageUserId: string | null | undefined;

const storageUserIdListeners = new Set<() => void>();

/**
 * Returns the current storage user id from the module cache, or from localStorage on first read.
 * @returns The signed-in user id, or `null` when signed out or on the server.
 */
export function getStorageUserId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (cachedStorageUserId === undefined) {
    cachedStorageUserId = localStorage.getItem(STORAGE_USER_POINTER_KEY);
  }
  return cachedStorageUserId;
}

/**
 * Updates the storage user id pointer, notifies subscribers, and keeps the module cache in sync.
 * @param userId The Better Auth user id, or `null` on sign-out.
 */
export function setStorageUserId(userId: string | null): void {
  if (getStorageUserId() === userId) {
    return;
  }
  cachedStorageUserId = userId;
  if (typeof window !== 'undefined') {
    if (userId === null) {
      localStorage.removeItem(STORAGE_USER_POINTER_KEY);
    } else {
      localStorage.setItem(STORAGE_USER_POINTER_KEY, userId);
    }
  }
  for (const listener of storageUserIdListeners) {
    listener();
  }
}

/**
 * Subscribes to storage user id changes (sign-in, sign-out, or user switch).
 * @param listener Called after `setStorageUserId` changes the id.
 * @returns Unsubscribe function.
 */
export function subscribeStorageUserId(listener: () => void): () => void {
  storageUserIdListeners.add(listener);
  return () => {
    storageUserIdListeners.delete(listener);
  };
}

/**
 * Maps a logical storage key to the namespaced localStorage key for the current or given user.
 * @param name Unscoped key name (e.g. `settings`, `bot-store`).
 * @param userId Override user id; defaults to {@link getStorageUserId}.
 * @returns Namespaced key (`{userId}:{name}` or `anon:{name}` when no user).
 */
export function scopedStorageKey(name: string, userId: string | null = getStorageUserId()): string {
  if (userId === null) {
    return `anon:${name}`;
  }
  return `${userId}:${name}`;
}

/**
 * Synchronously reads a logical key from the current user's namespace.
 * @param name Unscoped key name.
 * @returns Stored value or `null`.
 */
export function readUserScopedItem(name: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(scopedStorageKey(name));
}

/**
 * Clears the cached storage user id for unit tests. Does not remove listeners.
 */
export function resetStorageUserScopeForTests(): void {
  cachedStorageUserId = undefined;
}

/**
 * Zustand-compatible storage that reads and writes under the current user's namespace.
 * Does not read legacy unscoped keys.
 */
export const userScopedStorage: StateStorage = {
  getItem(name: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem(scopedStorageKey(name));
  },
  setItem(name: string, value: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(scopedStorageKey(name), value);
  },
  removeItem(name: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.removeItem(scopedStorageKey(name));
  },
};
